import { ApiAsset } from '@/components/app/cards/PacksList/types';
import { useUserState } from '@/hooks/store';
import { Guide } from '@/store/guide/types';
import { MeditationType, Program } from '@/store/program/types';
import { formatTime } from '@/utils/app/formatTime';

const ALWAYS_AVAILABLE_TYPES = new Set(['music', 'wisdom', 'meditation_with_music', 'timer']);

export enum GuideState {
	CourseCompleted = 'course_completed',
	CourseUnlocked = 'course_unlocked',
	CourseDisabled = 'course_disabled',
	CourseLocked = 'course_locked',
	Unlocked = 'unlocked',
	Locked = 'locked',
}

interface UseGuideInfo {
	(guide: Guide, program: Program, programProgress?: number): {
		guideState: GuideState;
		title: string;
		subtitle?: string;
		isCourse: boolean;
		isAvailable: boolean;
		isDisabled: boolean;
		image?: ApiAsset;
		duration: string | null;
	};
}

export const useGuideInfo: UseGuideInfo = (guide, program, programProgress = 0) => {
	const user = useUserState();

	if (!guide) {
		return {
			title: '',
			guideState: GuideState.Locked,
			isCourse: false,
			isAvailable: false,
			isDisabled: false,
			duration: null,
		};
	}

	const getGuideState = (): GuideState => {
		if (program?.content_type === 'Course') {
			if (guide.position <= programProgress) {
				return GuideState.CourseCompleted;
			}
			if (guide.position <= programProgress + 1) {
				return GuideState.CourseUnlocked;
			}
			if (user?.subscription?.valid) {
				return GuideState.CourseDisabled;
			}
			return GuideState.CourseLocked;
		}

		if (ALWAYS_AVAILABLE_TYPES.has(program?.meditation_type as string)) {
			return GuideState.Unlocked;
		}

		if (guide?.is_free) {
			return GuideState.Unlocked;
		}
		return GuideState.Locked;
	};

	const guideState = getGuideState();

	const isCourse = guideState.includes('course');
	const isAvailable = guideState.includes('unlocked') || guideState.includes('completed');
	const isDisabled = guideState.includes('disabled');

	const image = guide.cell_image ?? program?.guides?.[0]?.cell_image ?? program?.background_image;
	const duration =
		typeof guide.asset?.duration === 'number' ? `${formatTime(guide.asset.duration, true)}m` : null;

	const getGuideTitle = () => {
		if (isCourse) {
			return `${guide.position}. ${guide.title}`;
		}

		if (program.meditation_type === MeditationType.MasterClass) {
			return `${guide.position - 1}. ${guide.title}`;
		}

		return guide.title;
	};

	return {
		title: getGuideTitle(),
		subtitle: guide.subtitle,
		isCourse,
		isAvailable,
		isDisabled,
		guideState,
		image,
		duration,
	};
};
